// Betrieb › Radios › ein Radio (h-ui).
//
// Alles, was der Betreiber an einem Radio einstellt: Tarif und individuelle
// Abweichungen, Kontakt und Notiz, Sperren, Löschen vormerken (mit Kürzel
// als Bestätigung und 30 Tagen Karenz) und den Support-Zugriff.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { GRENZEN_NAMEN, betriebApi } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import { errorText } from "../../components/streaming-ui";
import { Meldungen, Sym } from "../../components/social-ui";
import { Badge, Button, Callout, Card, Page, PageHeader, Skeleton } from "../../components/ui";
import { GRENZEN_FELDER, Nutzungsbalken, StatusBadgeRadio, bytesText, datumText, grenzeText, zahlText } from "../../components/hosting-ui";
import type { BetriebRadioDetail, Grenzen, Tarif } from "../../types";
import { ZugriffsListe } from "../../components/support-freigabe";
import { NurBetreiber } from "./NurBetreiber";
import { DomainKarteBetrieb } from "../../components/domain-ui"; // h-domain
import { UmzugKarte } from "./UmzugKarte"; // h-umzug

export function RadioDetailPage({ id }: { id: string }): JSX.Element {
  const { user } = useAuth();
  const [radio, setRadio] = useState<BetriebRadioDetail | null>(null);
  const [tarife, setTarife] = useState<Tarif[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [nichtDa, setNichtDa] = useState(false);

  const laden = (): void => {
    betriebApi.radio(id).then(setRadio).catch((err) => {
      if (String(err instanceof Error ? err.message : err).startsWith("404")) setNichtDa(true);
      else setFehler(errorText(err));
    });
  };

  useEffect(() => {
    if (user?.role !== "super_admin") return;
    laden();
    betriebApi.tarife().then(setTarife).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, user?.role]);

  if (user?.role !== "super_admin") return <NurBetreiber titel="Radio" />;

  const melde = (text: string): void => {
    setHinweis(text);
    setFehler(null);
    window.setTimeout(() => setHinweis(null), 5000);
  };
  const tue = async (fn: () => Promise<unknown>, text: string): Promise<boolean> => {
    try {
      await fn();
      melde(text);
      laden();
      return true;
    } catch (err) {
      setFehler(errorText(err));
      return false;
    }
  };

  const zurueck = (
    <Link to="/betrieb/radios" className="btn btn-ghost -ml-2 mb-2">
      <Sym name="back" /> Alle Radios
    </Link>
  );

  if (nichtDa) {
    return (
      <Page className="max-w-5xl">
        {zurueck}
        <Callout tone="warning" title="Dieses Radio gibt es nicht (mehr)." />
      </Page>
    );
  }

  if (!radio) {
    return (
      <Page className="max-w-5xl">
        {zurueck}
        <Meldungen error={fehler} />
        <Skeleton className="mb-4 h-12 w-2/3" />
        <Skeleton className="mb-4 h-48" />
        <Skeleton className="h-48" />
      </Page>
    );
  }

  const tarif = tarife.find((t) => t.key === radio.tarif.key);

  return (
    <Page className="max-w-5xl">
      {zurueck}
      <PageHeader
        eyebrow="Betrieb · Radio"
        title={<span className="inline-flex flex-wrap items-center gap-3">{radio.name} <StatusBadgeRadio status={radio.status} /></span>}
        description={<>Kürzel <code className="font-mono">{radio.slug}</code> · angelegt {datumText(radio.created_at)} · letzte Anmeldung {datumText(radio.letzte_anmeldung, true)}</>}
      />

      <Meldungen error={fehler} notice={hinweis} onClear={() => setHinweis(null)} />

      {radio.status === "gesperrt" && (
        <Callout tone="danger" title="Gesperrt" className="mb-5">
          Das Radio sendet nicht, niemand aus dem Team kann sich anmelden, die Hörerseite zeigt „vorübergehend nicht erreichbar“.
          {radio.status_grund && <> Grund: <b>{radio.status_grund}</b></>}
          <div className="mt-2">
            <Button size="sm" onClick={() => void tue(() => betriebApi.entsperren(radio.id), "Entsperrt – das Radio läuft wieder.")}>Entsperren</Button>
          </div>
        </Callout>
      )}
      {radio.status === "loeschen_vorgemerkt" && (
        <Callout tone="warning" title={`Wird am ${datumText(radio.loeschen_ab)} gelöscht`} className="mb-5">
          Bis dahin ist es gesperrt und lässt sich zurückholen. Danach sind Radio, Team und alle Dateien endgültig weg.
          {radio.status_grund && <> Grund: <b>{radio.status_grund}</b></>}
          <div className="mt-2">
            <Button size="sm" onClick={() => void tue(() => betriebApi.loeschenAbbrechen(radio.id), "Löschung abgebrochen.")}>Löschung abbrechen</Button>
          </div>
        </Callout>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kennzahl label="Hörer jetzt" wert={zahlText(radio.hoerer_jetzt)} />
        <Kennzahl label="Kanäle senden" wert={radio.sendezustand.aktiv > 0 ? `${radio.sendezustand.laufend}/${radio.sendezustand.aktiv}` : "–"} />
        <Kennzahl label="Speicher" wert={bytesText(radio.speicher.mediathek_bytes + radio.speicher.bilder_bytes)} />
        <Kennzahl label="Traffic heute" wert={bytesText(radio.heute?.traffic_bytes)} />
      </div>

      <Card title="Nutzung" description={`Wirksame Grenzen: Tarif „${tarif?.name ?? radio.tarif.name ?? radio.tarif.key}“ mit den Abweichungen unten.`} className="mb-5">
        <Nutzungsbalken grenzen={radio.grenzen ?? tarif?.grenzen ?? {}} nutzung={radio.nutzung} kompakt />
      </Card>

      <TarifKarte radio={radio} tarife={tarife} onSpeichern={(body) => tue(() => betriebApi.aendern(radio.id, body), "Tarif gespeichert.")} />

      <KontaktKarte radio={radio} onSpeichern={(body) => tue(() => betriebApi.aendern(radio.id, body), "Gespeichert.")} />

      {/* h-domain: eigene Domains des Radios (nur lesen) */}
      <DomainKarteBetrieb tenantId={radio.id} />

      {/* h-umzug: exportieren (Umzug, Sicherung vor dem Löschen) */}
      <UmzugKarte radioId={radio.id} radioName={radio.name} />

      {radio.hat_betreiber ? (
        // Im eigenen Radio liegt der Betreiber-Zugang: Sperren oder Löschen
        // würde den Betreiber selbst aussperren, Support braucht es nicht.
        <Callout tone="info" title="Hier liegt dein Betreiber-Zugang">
          Dieses Radio lässt sich deshalb nicht sperren oder löschen, und einen Support-Zugriff brauchst du hier nicht – du bist schon drin.
        </Callout>
      ) : (
        <>
          <SupportKarte radio={radio} onFehler={setFehler} onNeuLaden={laden} />
          <Gefahrenzone
            radio={radio}
            onSperren={(grund) => tue(() => betriebApi.sperren(radio.id, grund), "Gesperrt.")}
            onLoeschen={(grund, bestaetigung) => tue(() => betriebApi.loeschen(radio.id, grund, bestaetigung), "Löschung vorgemerkt – in 30 Tagen ist das Radio weg.")}
          />
        </>
      )}
    </Page>
  );
}

function Kennzahl({ label, wert }: { label: string; wert: string }): JSX.Element {
  return (
    <div className="card p-4">
      <div className="text-[0.8rem] font-medium text-muted">{label}</div>
      <div className="mt-1.5 text-xl font-semibold tabular-nums text-primary">{wert}</div>
    </div>
  );
}

type Abweichung = { an: boolean; wert: string };

function TarifKarte({ radio, tarife, onSpeichern }: {
  radio: BetriebRadioDetail;
  tarife: Tarif[];
  onSpeichern: (body: { tarif: string; limits_json: Partial<Grenzen> }) => Promise<boolean>;
}): JSX.Element {
  const start = useMemo(() => {
    const a: Record<string, Abweichung> = {};
    for (const f of GRENZEN_FELDER) {
      const lim = radio.limits_json ?? {};
      const hat = Object.prototype.hasOwnProperty.call(lim, f.key);
      const v = lim[f.key];
      a[f.key] = f.art === "zahl"
        ? { an: hat, wert: v == null ? "" : String(v) }
        : { an: hat, wert: v === false ? "nein" : "ja" };
    }
    return a;
  }, [radio.limits_json]);
  const [tarif, setTarif] = useState(radio.tarif.key);
  const [abw, setAbw] = useState<Record<string, Abweichung>>(start);
  const [laeuft, setLaeuft] = useState(false);

  useEffect(() => { setTarif(radio.tarif.key); setAbw(start); }, [radio.tarif.key, start]);

  const basis = tarife.find((t) => t.key === tarif)?.grenzen ?? {};
  const setze = (k: string, p: Partial<Abweichung>): void => setAbw((a) => ({ ...a, [k]: { ...a[k]!, ...p } }));

  const ungueltig = GRENZEN_FELDER.some((f) => f.art === "zahl" && abw[f.key]?.an && abw[f.key]!.wert.trim() !== "" &&
    !(Number(abw[f.key]!.wert.replace(",", ".")) >= 0));

  const speichern = async (): Promise<void> => {
    const limits: Partial<Grenzen> = {};
    for (const f of GRENZEN_FELDER) {
      const a = abw[f.key];
      if (!a?.an) continue;
      if (f.art === "zahl") {
        (limits as Record<string, unknown>)[f.key] = a.wert.trim() === "" ? null : Number(a.wert.replace(",", "."));
      } else {
        (limits as Record<string, unknown>)[f.key] = a.wert === "ja";
      }
    }
    setLaeuft(true);
    await onSpeichern({ tarif, limits_json: limits });
    setLaeuft(false);
  };

  const anzahlAbw = GRENZEN_FELDER.filter((f) => abw[f.key]?.an).length;

  return (
    <Card
      title="Tarif & Abweichungen"
      description="Abweichungen gelten nur für dieses Radio und gehen dem Tarif vor. Leeres Zahlenfeld = unbegrenzt."
      actions={anzahlAbw > 0 ? <Badge tone="info">{anzahlAbw} abweichend</Badge> : undefined}
      className="mb-5"
    >
      <label className="block max-w-sm">
        <span className="field-label">Tarif</span>
        <select className="input" value={tarif} onChange={(e) => setTarif(e.target.value)}>
          {tarife.filter((t) => t.aktiv || t.key === tarif).map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}
          {!tarife.some((t) => t.key === tarif) && <option value={tarif}>{tarif}</option>}
        </select>
      </label>

      <ul className="mt-5 divide-y divide-default rounded-lg border border-default">
        {GRENZEN_FELDER.map((f) => {
          const a = abw[f.key]!;
          return (
            <li key={f.key} className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:gap-4">
              <label className="flex min-w-0 flex-1 items-center gap-2.5 text-sm">
                <input type="checkbox" checked={a.an} onChange={(e) => setze(f.key, { an: e.target.checked })} className="h-4 w-4 flex-shrink-0" />
                <span className="min-w-0">
                  <span className="block font-medium text-primary">{GRENZEN_NAMEN[f.key]}</span>
                  <span className="block text-xs text-muted">Tarif: {grenzeText(f.key, basis)}</span>
                </span>
              </label>
              {a.an && (f.art === "zahl" ? (
                <div className="flex items-center gap-2 sm:w-56">
                  <input
                    className="input min-w-0"
                    inputMode="decimal"
                    value={a.wert}
                    placeholder="unbegrenzt"
                    aria-label={`${GRENZEN_NAMEN[f.key]} für dieses Radio`}
                    onChange={(e) => setze(f.key, { wert: e.target.value })}
                  />
                  <span className="w-14 flex-shrink-0 text-xs text-muted">{f.einheit}</span>
                </div>
              ) : (
                <select className="input sm:w-56" value={a.wert} onChange={(e) => setze(f.key, { wert: e.target.value })} aria-label={`${GRENZEN_NAMEN[f.key]} für dieses Radio`}>
                  <option value="ja">enthalten</option>
                  <option value="nein">nicht enthalten</option>
                </select>
              ))}
            </li>
          );
        })}
      </ul>
      {ungueltig && <p className="mt-2 text-xs text-red-600 dark:text-red-400">Bitte nur Zahlen ab 0 eintragen – oder das Feld leer lassen für „unbegrenzt“.</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="primary" disabled={laeuft || ungueltig} onClick={() => void speichern()}>Speichern</Button>
        <Button variant="ghost" onClick={() => { setTarif(radio.tarif.key); setAbw(start); }}>Zurücksetzen</Button>
      </div>
      <p className="field-hint mt-3">Liegt das Radio danach über einer Grenze, bleibt Bestehendes erhalten – nur Neues wird verhindert.</p>
    </Card>
  );
}

function KontaktKarte({ radio, onSpeichern }: {
  radio: BetriebRadioDetail;
  onSpeichern: (body: { kontakt_email: string | null; notiz: string | null }) => Promise<boolean>;
}): JSX.Element {
  const [kontakt, setKontakt] = useState(radio.kontakt_email ?? "");
  const [notiz, setNotiz] = useState(radio.notiz ?? "");
  useEffect(() => { setKontakt(radio.kontakt_email ?? ""); setNotiz(radio.notiz ?? ""); }, [radio.kontakt_email, radio.notiz]);
  const geaendert = kontakt !== (radio.kontakt_email ?? "") || notiz !== (radio.notiz ?? "");
  return (
    <Card title="Kontakt & Notiz" description="Nur für dich als Betreiber sichtbar." className="mb-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">Kontakt-E-Mail</span>
          <input className="input" type="email" value={kontakt} onChange={(e) => setKontakt(e.target.value)} placeholder="verein@beispiel.de" />
          {radio.leitung && radio.leitung.length > 0 && (
            <span className="field-hint block">Radioleitung: {radio.leitung.map((a) => a.display_name ? `${a.display_name} (${a.email})` : a.email).join(", ")}</span>
          )}
        </label>
        <label className="block sm:row-span-2">
          <span className="field-label">Notiz</span>
          <textarea className="input" rows={4} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="z. B. Vertrag seit …, Ansprechpartner, Absprachen" />
        </label>
      </div>
      <Button variant="primary" className="mt-4" disabled={!geaendert}
        onClick={() => void onSpeichern({ kontakt_email: kontakt.trim() || null, notiz: notiz.trim() || null })}>
        Speichern
      </Button>
    </Card>
  );
}

function SupportKarte({ radio, onFehler, onNeuLaden }: { radio: BetriebRadioDetail; onFehler: (t: string) => void; onNeuLaden: () => void }): JSX.Element {
  const freigabeBis = radio.freigabe?.bis && Date.parse(radio.freigabe.bis) > Date.now() ? radio.freigabe.bis : null;
  const [art, setArt] = useState<"freigegeben" | "notfall">(freigabeBis ? "freigegeben" : "notfall");
  const [grund, setGrund] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setArt(freigabeBis ? "freigegeben" : "notfall"); }, [freigabeBis]);

  const mindest = art === "notfall" ? 20 : 5;
  const starten = async (): Promise<void> => {
    setLaeuft(true);
    try {
      await betriebApi.supportStarten({ id: radio.id, name: radio.name }, grund.trim(), art);
      // Der Tab lädt jetzt in der Sitzung des Radios neu.
    } catch (err) {
      onFehler(errorText(err));
      setLaeuft(false);
    }
  };

  return (
    <Card
      title="Support-Zugriff"
      description="Für höchstens 60 Minuten als Radioleitung dieses Radios arbeiten. Jede Änderung landet im Protokoll des Radios."
      actions={freigabeBis ? <Badge tone="success" dot>freigegeben bis {datumText(freigabeBis, true)}</Badge> : <Badge>keine Freigabe</Badge>}
      className="mb-5"
    >
      <div className="grid gap-4 sm:grid-cols-[minmax(0,14rem)_1fr]">
        <fieldset>
          <legend className="field-label">Art</legend>
          <div className="space-y-2 text-sm">
            <label className={`flex items-start gap-2 ${freigabeBis ? "" : "opacity-50"}`}>
              <input type="radio" name="support-art" className="mt-0.5" disabled={!freigabeBis} checked={art === "freigegeben"} onChange={() => setArt("freigegeben")} />
              <span><b className="font-medium text-primary">Freigegeben</b><span className="block text-xs text-muted">Die Radioleitung hat den Zugriff erlaubt.</span></span>
            </label>
            <label className="flex items-start gap-2">
              <input type="radio" name="support-art" className="mt-0.5" checked={art === "notfall"} onChange={() => setArt("notfall")} />
              <span><b className="font-medium text-primary">Notfall</b><span className="block text-xs text-muted">Ohne Freigabe – die Radioleitung wird sofort benachrichtigt.</span></span>
            </label>
          </div>
        </fieldset>
        <label className="block">
          <span className="field-label">Grund</span>
          <textarea className="input" rows={3} value={grund} onChange={(e) => setGrund(e.target.value)}
            placeholder={art === "notfall" ? "Was ist passiert, und warum kann es nicht warten? (mindestens 20 Zeichen)" : "z. B. Ticket 1234: Stundenuhr einrichten"} />
          <span className="field-hint block">{grund.trim().length < mindest ? `Noch ${mindest - grund.trim().length} Zeichen.` : "Die Radioleitung sieht diesen Grund im Protokoll."}</span>
        </label>
      </div>
      {art === "notfall" && (
        <Callout tone="warning" className="mt-4">Notfall-Zugriffe sind die Ausnahme. Die Radioleitung bekommt sofort eine Nachricht mit deinem Grund.</Callout>
      )}
      <Button variant="primary" className="mt-4" disabled={laeuft || grund.trim().length < mindest} onClick={() => void starten()}>
        <Sym name="shield" /> {laeuft ? "Wird gestartet …" : "Support-Zugriff starten"}
      </Button>
      {radio.zugriffe && radio.zugriffe.length > 0 && (
        <div className="mt-5">
          <div className="section-label mb-2">Bisherige Zugriffe</div>
          <ZugriffsListe zugriffe={radio.zugriffe} onBeenden={(zid) => { betriebApi.supportZugriffBeenden(zid).then(onNeuLaden).catch((err) => onFehler(errorText(err))); }} />
        </div>
      )}
    </Card>
  );
}

function Gefahrenzone({ radio, onSperren, onLoeschen }: {
  radio: BetriebRadioDetail;
  onSperren: (grund: string) => Promise<boolean>;
  onLoeschen: (grund: string, bestaetigung: string) => Promise<boolean>;
}): JSX.Element {
  const [offen, setOffen] = useState<"sperren" | "loeschen" | null>(null);
  const [grund, setGrund] = useState("");
  const [kuerzel, setKuerzel] = useState("");
  const [laeuft, setLaeuft] = useState(false);

  const schliessen = (): void => { setOffen(null); setGrund(""); setKuerzel(""); };
  const los = async (fn: () => Promise<boolean>): Promise<void> => {
    setLaeuft(true);
    const ok = await fn();
    setLaeuft(false);
    if (ok) schliessen();
  };

  return (
    <Card title="Sperren & Löschen" className="border-red-500/30">
      <div className="flex flex-wrap gap-2">
        {radio.status === "aktiv" && (
          <Button variant={offen === "sperren" ? "secondary" : "danger"} onClick={() => setOffen(offen === "sperren" ? null : "sperren")}>
            <Sym name="ban" /> Radio sperren
          </Button>
        )}
        {radio.status !== "loeschen_vorgemerkt" && (
          <Button variant={offen === "loeschen" ? "secondary" : "danger"} onClick={() => setOffen(offen === "loeschen" ? null : "loeschen")}>
            <Sym name="trash" /> Löschen vormerken
          </Button>
        )}
        {radio.status === "loeschen_vorgemerkt" && <p className="text-sm text-muted">Löschung ist vorgemerkt für den {datumText(radio.loeschen_ab)} – oben abbrechen.</p>}
      </div>

      {offen === "sperren" && (
        <div className="mt-4 space-y-3 rounded-lg border border-red-500/30 p-4">
          <p className="text-sm text-secondary">
            Gesperrt sendet das Radio nicht mehr, das Team kann sich nicht anmelden, Studio-Verbindungen werden abgewiesen
            und Hörer sehen „vorübergehend nicht erreichbar“. Daten bleiben erhalten; Entsperren geht jederzeit.
          </p>
          <label className="block">
            <span className="field-label">Grund (sieht das Team bei der Anmeldung)</span>
            <input className="input" value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Rechnung offen seit 60 Tagen" />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button variant="danger" disabled={laeuft || grund.trim().length < 3} onClick={() => void los(() => onSperren(grund.trim()))}>Jetzt sperren</Button>
            <Button variant="ghost" onClick={schliessen}>Abbrechen</Button>
          </div>
        </div>
      )}

      {offen === "loeschen" && (
        <div className="mt-4 space-y-3 rounded-lg border border-red-500/30 p-4">
          <p className="text-sm text-secondary">
            Das Radio wird sofort gesperrt und <b>nach 30 Tagen endgültig gelöscht</b> – mit Team, Sendeplänen, Mediathek,
            Bildern und Mitschnitten. Bis dahin lässt sich die Löschung abbrechen.
          </p>
          <label className="block">
            <span className="field-label">Grund</span>
            <input className="input" value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="z. B. Vertrag gekündigt zum 30.09." />
          </label>
          <label className="block">
            <span className="field-label">Zur Bestätigung das Kürzel eintippen: <code className="font-mono text-primary">{radio.slug}</code></span>
            <input className="input font-mono" value={kuerzel} onChange={(e) => setKuerzel(e.target.value)} autoComplete="off" spellCheck={false} />
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="danger"
              disabled={laeuft || grund.trim().length < 3 || kuerzel.trim() !== radio.slug}
              onClick={() => void los(() => onLoeschen(grund.trim(), kuerzel.trim()))}
            >
              Löschen vormerken
            </Button>
            <Button variant="ghost" onClick={schliessen}>Abbrechen</Button>
          </div>
        </div>
      )}
    </Card>
  );
}
